import { useState, useEffect, useCallback } from 'react';
import { Message, ChatSession } from '../types';

const STORAGE_KEY = 'ask-the-bill-chats';

export const useChatStorage = () => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Date strings need to be converted back to Date objects for messages
        const hydrated = parsed.map((session: any) => ({
          ...session,
          messages: session.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        }));
        setSessions(hydrated);
      } catch (e) {
        console.error('Failed to parse chat history', e);
      }
    }
  }, []);

  const saveSessions = (newSessions: ChatSession[]) => {
    setSessions(newSessions);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSessions));
  };

  const createNewSession = useCallback((billId: string, initialMessages: Message[] = []) => {
    const newSession: ChatSession = {
      id: Date.now().toString(),
      title: initialMessages.length > 0 ? initialMessages[0].content.slice(0, 30) + '...' : 'New Chat',
      billId,
      messages: initialMessages,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    
    saveSessions([newSession, ...sessions]);
    return newSession.id;
  }, [sessions]);

  const updateSession = useCallback((sessionId: string, messages: Message[]) => {
    const updatedSessions = sessions.map(session => {
      if (session.id === sessionId) {
        // Update title if it's the first user message being added
        let title = session.title;
        if (session.messages.length === 0 && messages.length > 0) {
          const firstMsg = messages[0];
          if (firstMsg.role === 'user') {
             title = firstMsg.content.slice(0, 30) + (firstMsg.content.length > 30 ? '...' : '');
          }
        }

        return {
          ...session,
          messages,
          title,
          updatedAt: Date.now(),
        };
      }
      return session;
    });
    // Sort by updatedAt desc
    updatedSessions.sort((a, b) => b.updatedAt - a.updatedAt);
    saveSessions(updatedSessions);
  }, [sessions]);

  const deleteSession = useCallback((sessionId: string) => {
    const newSessions = sessions.filter(s => s.id !== sessionId);
    saveSessions(newSessions);
  }, [sessions]);

  const getSession = useCallback((sessionId: string) => {
    return sessions.find(s => s.id === sessionId);
  }, [sessions]);

  return {
    sessions,
    createNewSession,
    updateSession,
    deleteSession,
    getSession
  };
};
